import elementResizeDetectorMaker from 'element-resize-detector';
import React, {
  ComponentType,
  createElement,
  CSSProperties,
  FC,
  PropsWithChildren,
  RefObject,
  useRef,
  useState,
} from 'react';

import {useInitialLayoutEffect} from '../../../core/src/helpers/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../core/src/helpers/react/reactHelpers';

const ERD = elementResizeDetectorMaker({
  strategy: 'scroll',
  debug: true,
  // These empty functions are needed so this doesn't log debug information to the console.
  reporter: {
    log: () => {},
    warn: () => {},
    error: () => {},
  },
});

/*
 * Hook.
 */

export interface Size {
  width: number;
  height: number;
}

// TODO: Deprecate `useSize` in favor of `useMeasureElement`.
//       Please consider using the `useMeasureElement` hook instead.
export function useSize(wrapperRef: RefObject<HTMLElement>) {
  const [size, setSize] = useState<Size>();

  useSizeListener({
    wrapperRef,
    listener: setSize,
    shouldReportInitialSize: true,
  });

  return size;
}

interface SizeListenerOptions {
  wrapperRef: RefObject<HTMLElement>;
  listener: HandlerOf<Size>;
  shouldReportInitialSize?: boolean;
}

/**
 * Observes size changes on `options.ref` and calls `options.listener` when height/width changes.
 *
 * NOTE: This hook will NOT pick up element changes (i.e the ref being replaced by another DOM element).
 */
export function useSizeListener(options: SizeListenerOptions) {
  // Always maintain a reference to the latest version of the function.
  const listenerRef = useRef(options.listener);
  listenerRef.current = options.listener;

  useInitialLayoutEffect(() => {
    const element = options.wrapperRef.current;
    if (!element) {
      return undefined;
    }

    let prevSize: Size | undefined;

    const maybeNotifyListener = () => {
      const {width, height} = element.getBoundingClientRect();
      const size = {width, height};

      if (prevSize && size.width === prevSize.width && prevSize.height === size.height) {
        return;
      }

      prevSize = size;

      const listener = listenerRef.current;
      listener(size);
    };

    // Synchronously notify the listener about our initial size.
    if (options.shouldReportInitialSize) {
      maybeNotifyListener();
    }

    // Notify the listener for future size changes.
    ERD.listenTo(element, maybeNotifyListener);

    return () => {
      ERD.uninstall(element);
    };
  });
}

/*
 * Component.
 */

interface SizeListenerProps {
  onSize: HandlerOf<Size>;
  style?: CSSProperties;
  className?: string;
}

export const SizeListener: FC<PropsWithChildren<SizeListenerProps>> = ({
  onSize,
  children,
  className,
  style,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useSizeListener({
    wrapperRef,
    listener: onSize,
  });

  return (
    <div ref={wrapperRef} className={className} style={style}>
      {children}
    </div>
  );
};

/*
 * HOC.
 */

export interface WithSizeProps {
  wrapperRef: RefObject<HTMLDivElement>;
  size?: Size;
}

export function withSize<T extends WithSizeProps>(
  Component: ComponentType<PropsWithChildren<T>>,
): ComponentType<PropsWithChildren<Omit<T, keyof WithSizeProps>>> {
  return function WithSize(props) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState<Size>();

    useSizeListener({
      wrapperRef,
      listener: setSize,
    });

    return createElement(Component, {
      ...props,
      wrapperRef,
      size,
    } as T);
  };
}
